<template>
  <Sheet>
    <template #leading>
      <Sheet.Button :icon="CloseIcon" @click="handleBack" />
    </template>
    <template #title>Copyright registration</template>
    <template #trailing>
      <Sheet.Button
        variant="primary"
        :icon="CheckmarkIcon"
        @click="handleConfirm"
      />
    </template>

    <Sheet.Body class="px-0! mt-4">
      <div class="ml-[60px] mr-5 h-px bg-gray-800/10" />

      <button
        type="button"
        class="flex w-full items-center gap-4 px-6 py-[14px] text-left"
        @click="openOwner"
      >
        <div class="min-w-0 flex-1">
          <p
            class="mb-1 ml-9 font-medium text-[13px] leading-[17px] text-gray-500"
          >
            Copyright owner
          </p>
          <div class="flex items-center gap-4">
            <Avatar :src="owner?.avatarUrl" :size="20" class="shrink-0" />
            <p
              class="min-w-0 flex-1 truncate font-secondary text-[16px] leading-[18px] text-gray-800"
            >
              {{ owner?.name }}
            </p>
          </div>
        </div>
        <span
          class="shrink-0 font-medium text-[16px] leading-[18px] text-green-700"
        >
          Change
        </span>
      </button>

      <div class="ml-[60px] mr-5 h-px bg-gray-800/10" />

      <button
        v-if="isMultiple"
        type="button"
        class="flex w-full items-start gap-4 px-6 py-[14px] text-left"
        @click="openFiles"
      >
        <LicenseAddOnIcon class="size-5 shrink-0 text-gray-800" />
        <div class="min-w-0 flex-1">
          <p class="font-secondary text-[16px] leading-[18px] text-gray-800">
            {{ files.length }} files to be registered
          </p>
          <p class="text-[13px] text-gray-600 leading-[17px]">
            <span class="font-medium">Important:</span>
            Filename cannot be changed later
          </p>
        </div>
        <ChevronRightIcon
          class="size-[18px] shrink-0 self-center text-gray-400"
        />
      </button>

      <div v-else class="flex items-start gap-4 px-6 py-[14px]">
        <FileAddIcon class="size-5 shrink-0 text-gray-800" />
        <div class="min-w-0 flex-1">
          <p
            class="truncate font-secondary text-[16px] leading-[18px] text-gray-800 mb-1"
          >
            {{ firstFileName }}
          </p>
          <p class="text-[13px] leading-[17px] text-gray-600">
            <span class="font-medium">Important:</span>
            Filename cannot be changed later
          </p>
        </div>
      </div>

      <div class="ml-[60px] mr-5 h-px bg-gray-800/10" />

      <button
        type="button"
        class="flex w-full items-center gap-4 px-6 py-[15px] text-left"
        @click="openFolder"
      >
        <div class="w-5 shrink-0">
          <Thumbnail type="folder" />
        </div>
        <p
          class="min-w-0 flex-1 truncate font-secondary text-[16px] leading-[18px] text-gray-800"
        >
          {{ folderName }}
        </p>
        <span
          class="shrink-0 font-medium text-[16px] leading-[18px] text-green-700"
        >
          Change
        </span>
      </button>

      <div class="ml-[60px] mr-5 h-px bg-gray-800/10" />

      <div class="flex items-start gap-4 px-6 py-[14px]">
        <LockIcon
          class="size-5 shrink-0 text-gray-800"
          :class="isBasic && 'opacity-50'"
        />
        <div class="min-w-0 flex-1 pr-3">
          <div class="flex items-center gap-1.5">
            <p
              class="text-[16px] leading-[18px] text-gray-800"
              :class="isBasic && 'opacity-50'"
            >
              Privacy
            </p>
            <Pill v-if="isBasic" variant="success">
              Pro Membership required
            </Pill>
          </div>
          <p
            class="mt-1.5 text-[13px] leading-[17px] text-gray-600"
            :class="isBasic && 'opacity-50'"
          >
            <span class="font-medium">Make private. </span>
            Only you and people you invite can see the file and description.
          </p>
        </div>
        <Toggle v-model="isPrivate" size="lg" :disabled="isBasic" />
      </div>

      <div class="px-6 pt-0.5 pb-2">
        <div
          class="flex items-start rounded-[12px] border border-[#f2f0ee] bg-[#f9f9fa] px-[14px] py-2.5"
        >
          <InfoSmallIcon class="mt-1 mr-2 size-3 shrink-0 text-gray-500" />
          <p class="text-[15px] leading-5 text-[#69696e]">
            Add authors, co-owners, and more details after your work is
            registered.
          </p>
        </div>
      </div>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Sheet, Avatar, Pill, Toggle, Thumbnail, useSheet } from '@dacr/library'
import {
  FileAddIcon,
  LicenseAddOnIcon,
  LockIcon,
  ChevronRightIcon,
  CloseIcon,
  CheckmarkIcon,
  InfoSmallIcon,
} from '@dacr/icons'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import { useUploadStore } from '@/features/upload/store'
import { useExplorerStore } from '@/features/explorer/store'
import { useUserStore } from '@/shared/stores/user'
import {
  UserPermission,
  useCopyrightOwnersNewQuery,
} from '@/shared/graphql/legacy/generated'

const { closeAll, triggerEvent } = useSheet()
const uploadStore = useUploadStore()
const explorerStore = useExplorerStore()
const userStore = useUserStore()

const { result } = useCopyrightOwnersNewQuery()

const draft = computed(() => uploadStore.draft)

const owner = computed(() => {
  const id = draft.value?.copyrightOwnerId
  const owners = result.value?.copyrightOwnersNew.items ?? []
  return owners.find((o) => o.id === id) ?? explorerStore.selectedCopyrightOwner
})

const files = computed(() => draft.value?.files ?? [])
const isMultiple = computed(() => files.value.length > 1)
const firstFileName = computed(() => files.value[0]?.name ?? '')
const folderName = computed(() => draft.value?.folderName || 'Dacr')

const isBasic = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const isPrivate = computed({
  get: () => !isBasic.value && !!draft.value?.isPrivate,
  set: (value) => uploadStore.updateDraft({ isPrivate: value }),
})

const openOwner = () => openSheet(Sheets.UploadSelectOwner, { stack: true })
const openFiles = () => openSheet(Sheets.UploadFilesList, { stack: 'cover' })
const openFolder = () =>
  openSheet(Sheets.UploadSelectFolder, { stack: 'cover' })

const handleConfirm = () => triggerEvent('success')

const handleBack = () => closeAll()
</script>
